import { useState, useEffect } from 'react';
import { getAdminClients, updateClient } from '@/lib/api';
import { CheckCircle, Clock, XCircle, UserCheck, Phone, Edit2, Check, X, Crown, Star, Gem } from 'lucide-react';

const RANKS = [
  { value: 'pro', label: 'PRO', color: 'bg-[#00A5FF]/10 text-[#00A5FF]', icon: Star },
  { value: 'gold', label: 'GOLD', color: 'bg-[#F5C400]/15 text-[#F5C400]', icon: Crown },
  { value: 'grossiste', label: 'GROSSISTE', color: 'bg-[#E8231B]/10 text-[#E8231B]', icon: Gem },
];

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState('');
  const [editingField, setEditingField] = useState(null); // {id, field}
  const [editValue, setEditValue] = useState('');

  const fetch = () => { getAdminClients({ status: filter || undefined }).then(r => setClients(r.data)); };
  useEffect(() => { fetch(); }, [filter]);

  const handleValidate = async (id) => {
    await updateClient(id, { status: 'validated', rank: 'pro' });
    fetch();
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter ce client ?')) return;
    await updateClient(id, { status: 'rejected' });
    fetch();
  };

  const startEdit = (id, field, currentValue) => {
    setEditingField({ id, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = async () => {
    if (!editingField) return;
    const { id, field } = editingField;
    await updateClient(id, { [field]: editValue });
    setEditingField(null);
    fetch();
  };

  const handleRankChange = async (id, rank) => {
    await updateClient(id, { rank });
    fetch();
  };

  const getRankInfo = (rank) => RANKS.find(r => r.value === rank) || RANKS[0];

  return (
    <div data-testid="admin-clients" className="p-6 lg:p-8">
      <h1 className="text-2xl font-extrabold text-[#111] tracking-tight mb-6">Gestion des clients</h1>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {[
          { value: '', label: 'Tous' },
          { value: 'pending', label: 'En attente' },
          { value: 'validated', label: 'Validés' },
          { value: 'rejected', label: 'Rejetés' },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 text-xs font-semibold border rounded-sm transition-colors duration-200 ${filter === f.value ? 'bg-[#00A5FF] text-white border-[#00A5FF]' : 'border-black/15 text-[#111] hover:bg-[#F8F9FA]'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50 border-b border-black/10">
              <th className="text-left px-3 py-2 font-semibold">Entreprise</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Tél</th>
              <th className="text-left px-3 py-2 font-semibold">TVA</th>
              <th className="text-center px-3 py-2 font-semibold">Statut</th>
              <th className="text-center px-3 py-2 font-semibold">Rang</th>
              <th className="text-center px-3 py-2 font-semibold">Remise</th>
              <th className="text-left px-3 py-2 font-semibold">Note</th>
              <th className="text-left px-3 py-2 font-semibold">Inscrit</th>
              <th className="text-right px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const rankInfo = getRankInfo(c.rank);
              const RankIcon = rankInfo.icon;
              return (
                <tr key={c.id} className="border-t border-black/5 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <td className="px-3 py-3 font-medium">{c.company_name}</td>
                  <td className="px-3 py-3 text-black/60 text-xs">{c.email}</td>

                  {/* Phone */}
                  <td className="px-3 py-3">
                    {editingField?.id === c.id && editingField?.field === 'phone' ? (
                      <div className="flex items-center gap-1">
                        <input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-28 px-1 py-0.5 text-xs border border-black/15" placeholder="+32..." />
                        <button onClick={saveEdit} className="text-green-600"><Check size={12} /></button>
                        <button onClick={() => setEditingField(null)} className="text-black/30"><X size={12} /></button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(c.id, 'phone', c.phone)} className="text-xs text-black/50 hover:text-[#00A5FF] flex items-center gap-1">
                        {c.phone ? <><Phone size={10} /> {c.phone}</> : <span className="text-black/20">+ Tél</span>}
                      </button>
                    )}
                  </td>

                  <td className="px-3 py-3 text-xs font-mono text-black/40">{c.vat_number || '-'}</td>

                  {/* Status */}
                  <td className="px-3 py-3 text-center">
                    {c.status === 'validated' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600"><CheckCircle size={12} /> Validé</span>}
                    {c.status === 'pending' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F5C400]"><Clock size={12} /> Attente</span>}
                    {c.status === 'rejected' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8231B]"><XCircle size={12} /> Rejeté</span>}
                  </td>

                  {/* Rank */}
                  <td className="px-3 py-3 text-center">
                    {c.status === 'validated' ? (
                      <select
                        value={c.rank || 'pro'}
                        onChange={(e) => handleRankChange(c.id, e.target.value)}
                        className={`text-xs font-bold px-2 py-1 border-0 cursor-pointer rounded-sm ${rankInfo.color}`}
                      >
                        {RANKS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    ) : (
                      <span className="text-xs text-black/20">-</span>
                    )}
                  </td>

                  {/* Discount */}
                  <td className="px-3 py-3 text-center">
                    <span className="text-sm font-bold">{c.discount_percent || 0}%</span>
                  </td>

                  {/* Notes */}
                  <td className="px-3 py-3">
                    {editingField?.id === c.id && editingField?.field === 'notes' ? (
                      <div className="flex items-center gap-1">
                        <input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-40 px-1 py-0.5 text-xs border border-black/15" placeholder="Note interne..." />
                        <button onClick={saveEdit} className="text-green-600"><Check size={12} /></button>
                        <button onClick={() => setEditingField(null)} className="text-black/30"><X size={12} /></button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(c.id, 'notes', c.notes)} className="text-xs text-black/40 hover:text-[#00A5FF] max-w-[150px] truncate block">
                        {c.notes || <span className="text-black/15">+ Note</span>}
                      </button>
                    )}
                  </td>

                  <td className="px-3 py-3 text-xs text-black/40">{new Date(c.created_at).toLocaleDateString('fr-BE')}</td>

                  {/* Actions */}
                  <td className="px-3 py-3 text-right">
                    {c.status === 'pending' && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleValidate(c.id)} data-testid={`validate-${c.id}`} className="p-1.5 text-green-600 hover:bg-green-50 rounded-sm" title="Valider (PRO)">
                          <UserCheck size={14} />
                        </button>
                        <button onClick={() => handleReject(c.id)} className="p-1.5 text-[#E8231B] hover:bg-red-50 rounded-sm" title="Rejeter">
                          <XCircle size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {clients.length === 0 && (
          <div className="p-8 text-center text-sm text-black/40">Aucun client trouvé</div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-6 text-xs text-black/40">
        {RANKS.map(r => {
          const Icon = r.icon;
          return (
            <div key={r.value} className="flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 font-bold rounded-sm ${r.color}`}><Icon size={10} /> {r.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
